import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, input, InputSignal, Signal, WritableSignal } from '@angular/core';
import { binarySearch } from '@utils/binary-search';
import { ChartBinding } from '@utils/chart-interactive/chart-controller.directive';
import { CURRENT_CHART_TIMEFRAME } from '@utils/chart-interactive/chart-timeframe-controller.directive';
import { ChartTimeframe, ChartTimeframeSelector } from '@utils/chart-interactive/chart-timeframe-selector';
import { SymbolTimestamps } from '@utils/charts';
import flatpickr from 'flatpickr';
import { DatepickerDirective } from 'src/app/common/directives/datepicker.directive';
import iconLibrary from 'src/app/common/iconLibrary';
import { DatepickerDatePipe } from 'src/app/common/pipes/datepicker-date.pipe';

export type DatepickerVariant = 'start' | 'end'

@Component({
  selector: 'wct-datepicker',
  standalone: true,
  imports: [DatepickerDirective, DatepickerDatePipe],
  templateUrl: './datepicker.component.html',
  styleUrl: './datepicker.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DatepickerComponent {
  public range: InputSignal<ChartTimeframe> = input.required<ChartTimeframe>();
  public variant: InputSignal<DatepickerVariant> = input.required<DatepickerVariant>();

  protected readonly currentTimeframe: WritableSignal<ChartTimeframe> = inject(CURRENT_CHART_TIMEFRAME);
  protected readonly timeframeController: ChartTimeframeSelector = inject(ChartTimeframeSelector);
  protected readonly iconLibrary = iconLibrary;

  private readonly chart: ChartBinding = inject(ChartBinding);

  protected readonly displayedTimeframe: Signal<number | null> = computed(() => {
    const variant: DatepickerVariant = this.variant();
    const timeframe: ChartTimeframe = this.currentTimeframe();

    if (!timeframe) {
      return null;
    }

    return variant === 'start' ? timeframe.from : timeframe.to;
  });

  protected readonly rangeMin: Signal<string> = computed(() => {
    return this.range().fromStr;
  });

  protected readonly rangeMax: Signal<string> = computed(() => {
    return this.currentTimeframe().toStr;
  });

  protected readonly selectedDate: Signal<string> = computed(() =>
    this.variant() === 'start' ? this.currentTimeframe().fromStr : this.currentTimeframe().toStr
  );

  protected readonly isDateDisabled = (date: Date): boolean => {
    // Flatpickr spits out dates 0 o'clock in the current timezone, timestamps are 0 o'clock utc
    const utcTime: number = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    const chart: Highcharts.Chart | undefined = this.chart.chart?.();

    if (chart) {
      const timestamps: number[] = chart.options[SymbolTimestamps];
      const dateInTimestamps: boolean = binarySearch(timestamps, utcTime)[0] !== undefined;
      return !dateInTimestamps;
    }

    return false;
  }

  protected setDate(input: string): void {
    const date: Date = flatpickr.parseDate(input, DatepickerDirective.DATA_FORMAT);
    const dateStr: string = input;
    let newTimeframe: ChartTimeframe;

    if (this.variant() === 'start') {
      newTimeframe = {
        ...this.currentTimeframe(),
        from: date.getTime(),
        fromStr: dateStr,
      };

      if (newTimeframe.from > newTimeframe.to) {
        newTimeframe.to = newTimeframe.from;
        newTimeframe.toStr = newTimeframe.fromStr;
      }
    } else {
      newTimeframe = {
        ...this.currentTimeframe(),
        to: date.getTime(),
        toStr: dateStr,
      };

      if (newTimeframe.to < newTimeframe.from) {
        newTimeframe.from = newTimeframe.to;
        newTimeframe.fromStr = newTimeframe.toStr;
      }
    }

    this.timeframeController.updateRange(newTimeframe);
  }
}
